import React from 'react';
import { SimulationData } from '../App';

interface Market {
  rank: number;
  flag: string;
  country: string;
  score: number;
  profitable: string;
  revenue: string;
  verdict: string;
  tag: 'go' | 'caution';
  recommended?: boolean;
}

interface Feature {
  title: string;
  description: string;
}

interface MultiMarketComparisonProps {
  onStartSimulation: (data: SimulationData) => void;
}

const MultiMarketComparison: React.FC<MultiMarketComparisonProps> = ({ onStartSimulation }) => {
  const markets: Market[] = [
    { rank: 1, flag: '🇳🇱', country: 'Netherlands', score: 8.4, profitable: 'Month 7', revenue: '€412K', verdict: 'Strong GO', tag: 'go', recommended: true },
    { rank: 2, flag: '🇩🇰', country: 'Denmark', score: 7.8, profitable: 'Month 8', revenue: '€298K', verdict: 'GO', tag: 'go' },
    { rank: 3, flag: '🇩🇪', country: 'Germany', score: 7.0, profitable: 'Month 10', revenue: '€353K', verdict: 'Conditional', tag: 'caution' },
    { rank: 4, flag: '🇵🇱', country: 'Poland', score: 6.5, profitable: 'Month 11', revenue: '€215K', verdict: 'Conditional', tag: 'caution' }
  ];

  const features: Feature[] = [
    { title: 'Automatic market comparison', description: 'We automatically compare your target market against multiple markets so you know if a better market exists or which to prioritise next.' },
    { title: 'Side-by-side financials', description: 'See revenue, operating costs, and break-even timelines for each market side by side so you can make a direct comparison without guessing.' },
    { title: 'AI-powered entry scoring', description: 'Every market gets a single entry score so you can instantly see which ones are worth pursuing and which ones are not.' },
    { title: 'Expansion sequencing', description: 'Know which market to enter first for the fastest return and which to save for later so you never waste runway on the wrong move.' }
  ];

  return (
    <section className="mmc-section">
      <div className="mmc-inner">
        <div className="section-label"><span>Multi-Market Comparison</span></div>
        <h2 className="section-title">Your target market might not<br /><em>be your best market.</em></h2>
        <p className="section-sub">We automatically compare your target market against multiple markets so you see where you reach profitability fastest, how your operating costs compare, and which market gives you the strongest return.</p>
        <div className="mmc-demo">
          <div className="mmc-visual">
            <div className="mmc-visual-header">
              <span className="mmc-visual-title">Germany vs. Alternatives</span>
            </div>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
              <table className="mmc-table" style={{ minWidth: '420px' }}>
                <thead>
                  <tr>
                    <th>#</th><th>Market</th><th>Score</th><th>Profitable</th><th>12-Mo Rev</th><th>Verdict</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {markets.map((market, index) => (
                    <tr key={index} className={market.recommended ? 'mmc-winner' : ''}>
                      <td>{market.rank}</td>
                      <td>
                        {market.flag} {market.country}
                        {market.recommended && <span className="mmc-tag go" style={{ marginLeft: '4px', fontSize: '7px', verticalAlign: 'middle' }}>Recommended</span>}
                      </td>
                      <td>
                        <div className="mmc-score">
                          <span>{market.score.toFixed(1)}</span>
                          <div className="mmc-score-bar">
                            <div className={`mmc-score-fill ${market.score >= 7.5 ? 'high' : 'mid'}`} style={{ width: `${market.score * 10}%` }}></div>
                          </div>
                        </div>
                      </td>
                      <td>{market.profitable}</td>
                      <td>{market.revenue}</td>
                      <td><span className={`mmc-tag ${market.tag}`}>{market.verdict}</span></td>
                      <td>
                        <a href="#" className="mmc-insights-link" onClick={(e) => { e.preventDefault(); onStartSimulation({ country: market.country }); }}>View Insights</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="mmc-features">
            {features.map((feature, index) => (
              <div key={index} className="mmc-feature">
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '36px' }}>
          <a href="#" style={{ display: 'inline-block', background: 'var(--gold)', color: 'var(--forest)', fontSize: '13px', fontWeight: 600, padding: '12px 28px', borderRadius: '8px', textDecoration: 'none', transition: 'all 0.25s' }}>Get Your Free Simulation</a>
        </div>
      </div>
    </section>
  );
};

export default MultiMarketComparison;
